import { LuInfo, LuUser, LuUsers, LuCircleAlert, LuPaperclip } from 'react-icons/lu';
import { BASE_URL } from '@/lib/api';
import type { Case } from '@/types';
import type { CaseRow } from '..';
import { SectionCard, Field } from './shared';

/* ─── Case Details Tab ─── */

export function CaseDetailsTab({ caseRow, fullCase }: { caseRow: CaseRow; fullCase: Case | null }) {
    const applicant = fullCase?.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant') ?? null;
    const respondent = fullCase?.parties?.find((p) => p.role === 'Respondent' || p.role === 'Defendant') ?? null;

    return (
        <div className="space-y-5">
            <SectionCard icon={<LuInfo size={18} className="text-secondary" />} title="General">
                <div className="grid grid-cols-2 gap-y-4">
                    <Field label="Date" value={caseRow.date} />
                    <Field label="Amount Paid" value={caseRow.amountPaid} />
                    {fullCase?.caseNumber && <Field label="Case Number" value={fullCase.caseNumber} />}
                    {fullCase?.suitNumber && <Field label="Suit Number" value={fullCase.suitNumber} />}
                </div>
                {fullCase?.description && (
                    <div className="mt-4">
                        <p className="text-[13px] font-semibold text-secondary">Description</p>
                        <p className="mt-1 text-[14px] leading-relaxed text-[#555]">{fullCase.description}</p>
                    </div>
                )}
            </SectionCard>

            {applicant && (
                <SectionCard icon={<LuUser size={18} className="text-secondary" />} title="Applicant">
                    <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-3">
                        <Field label="Name" value={applicant.name} />
                        <Field label="Party Type" value={applicant.partyType} />
                        <Field label="Email" value={applicant.email} />
                        <Field label="Phone Number" value={applicant.phone} />
                        <Field label="Address" value={applicant.address} />
                    </div>
                </SectionCard>
            )}

            {respondent && (
                <SectionCard icon={<LuUsers size={18} className="text-secondary" />} title="Respondent">
                    <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-3">
                        <Field label="Name" value={respondent.name} />
                        <Field label="Party Type" value={respondent.partyType} />
                        <Field label="Email" value={respondent.email} />
                        <Field label="Phone Number" value={respondent.phone} />
                        <Field label="Address" value={respondent.address} />
                    </div>
                </SectionCard>
            )}

            <SectionCard icon={<LuCircleAlert size={18} className="text-secondary" />} title="Dispute">
                <div className="grid grid-cols-2 gap-y-4">
                    <Field label="Case Category" value={caseRow.caseCategory} />
                    <Field label="Dispute Category" value={caseRow.disputeCategory} />
                </div>
            </SectionCard>

            {fullCase?.evidence && fullCase.evidence.length > 0 && (
                <SectionCard icon={<LuPaperclip size={18} className="text-secondary" />} title="Evidence">
                    {(() => {
                        const mandatory = fullCase.evidence.filter((ev) => ev.mandatoryDocType);
                        const others = fullCase.evidence.filter((ev) => !ev.mandatoryDocType);
                        const fileLink = (ev: (typeof fullCase.evidence)[number]) => {
                            const href = ev.fileUrl ? `${BASE_URL}${ev.fileUrl}` : undefined;
                            return href ? (
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-secondary underline underline-offset-2 break-all"
                                >
                                    {ev.fileName}
                                </a>
                            ) : (
                                <span className="text-[#555] break-all">{ev.fileName}</span>
                            );
                        };
                        return (
                            <div className="space-y-3">
                                {mandatory.map((ev) => (
                                    <div key={ev.id} className="flex items-baseline gap-2 text-[13px]">
                                        <span className="font-semibold text-secondary">{ev.mandatoryDocType}:</span>
                                        {fileLink(ev)}
                                    </div>
                                ))}
                                {others.length > 0 && (
                                    <div className={mandatory.length > 0 ? 'mt-2' : ''}>
                                        {mandatory.length > 0 && (
                                            <p className="mb-2 text-[12px] font-semibold text-secondary">
                                                Additional files
                                            </p>
                                        )}
                                        <div className="flex flex-wrap gap-2">
                                            {others.map((ev) => {
                                                const href = ev.fileUrl ? `${BASE_URL}${ev.fileUrl}` : undefined;
                                                return href ? (
                                                    <a
                                                        key={ev.id}
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="rounded-md bg-[#f5f5f5] px-3 py-1.5 text-[12px] text-secondary underline underline-offset-2 hover:bg-[#eee]"
                                                    >
                                                        {ev.fileName}
                                                    </a>
                                                ) : (
                                                    <span
                                                        key={ev.id}
                                                        className="rounded-md bg-[#f5f5f5] px-3 py-1.5 text-[12px] text-[#555]"
                                                    >
                                                        {ev.fileName}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </SectionCard>
            )}
        </div>
    );
}
